import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save, Loader2, ListChecks, MessageSquareReply } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { crmService } from '@/lib/api';

const ItemRow = ({ item, onChange, onRemove, extra, testId }) => (
  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5" data-testid={testId}>
    <input type="color" value={item.color || '#64748b'} onChange={(e) => onChange({ color: e.target.value })} className="h-7 w-8 rounded border-0 bg-transparent cursor-pointer" title="Color" />
    <Input value={item.name} onChange={(e) => onChange({ name: e.target.value })} className="h-8 text-xs uppercase flex-1 min-w-[140px]" placeholder="NOMBRE" data-testid={`${testId}-name`} />
    {extra}
    <label className="flex items-center gap-1 text-[11px] text-slate-500"><Switch checked={item.is_active !== false} onCheckedChange={(v) => onChange({ is_active: v })} />Activo</label>
    <button type="button" onClick={onRemove} className="text-slate-300 hover:text-red-600" title="Quitar" data-testid={`${testId}-remove`}><Trash2 className="w-4 h-4" /></button>
  </div>
);

const SettingsGestion = () => {
  const [states, setStates] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    crmService.settings().then((r) => { setStates(r.data.account_states); setResponses(r.data.response_types); }).catch(() => toast.error('No se pudo cargar la configuración del CRM')).finally(() => setLoading(false));
  }, []);

  const upd = (setter) => (idx, patch) => setter((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const rm = (setter) => (idx) => setter((arr) => arr.filter((_, i) => i !== idx));
  const updState = upd(setStates); const updResp = upd(setResponses);

  const save = async () => {
    if (states.some((s) => !s.name.trim()) || responses.some((r) => !r.name.trim())) { toast.error('Todos los ítems necesitan nombre'); return; }
    setSaving(true);
    try {
      const r = await crmService.updateSettings({ account_states: states, response_types: responses });
      setStates(r.data.account_states); setResponses(r.data.response_types);
      toast.success('Configuración del CRM guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="settings-gestion">
      <Card className="bg-white border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-1"><ListChecks className="w-5 h-5 text-teal-600" /><h3 className="font-semibold text-slate-900">Estados de la cuenta</h3></div>
        <p className="text-xs text-slate-500 mb-3">Situación general del titular (ej. NEGOCIANDO, NIEGA DEUDA). Marcá uno como estado inicial.</p>
        <div className="space-y-2">
          {states.map((s, i) => (
            <ItemRow key={s.id || `new-${i}`} item={s} testId={`crm-state-row-${i}`} onChange={(p) => updState(i, p)} onRemove={() => rm(setStates)(i)}
              extra={<label className="flex items-center gap-1 text-[11px] text-slate-500"><input type="radio" name="default-state" checked={!!s.is_default} onChange={() => setStates((arr) => arr.map((x, j) => ({ ...x, is_default: j === i })))} />Inicial</label>} />
          ))}
        </div>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => setStates([...states, { name: '', color: '#64748b', is_active: true }])} data-testid="crm-add-state"><Plus className="w-4 h-4 mr-1" />Agregar estado</Button>
      </Card>

      <Card className="bg-white border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-1"><MessageSquareReply className="w-5 h-5 text-violet-600" /><h3 className="font-semibold text-slate-900">Tipos de respuesta</h3></div>
        <p className="text-xs text-slate-500 mb-3">Resultado de cada gestión (ej. NO RESPONDE, PROMESA DE PAGO). Opcionalmente cambian el estado de la cuenta, exigen fecha y monto de promesa, o marcan el contacto como inválido.</p>
        <div className="space-y-2">
          {responses.map((r, i) => (
            <ItemRow key={r.id || `new-${i}`} item={r} testId={`crm-response-row-${i}`} onChange={(p) => updResp(i, p)} onRemove={() => rm(setResponses)(i)}
              extra={
                <>
                  <Select value={r.sets_state || '__none__'} onValueChange={(v) => updResp(i, { sets_state: v === '__none__' ? null : v })}>
                    <SelectTrigger className="h-8 w-44 text-[11px]"><SelectValue placeholder="Cambia estado a…" /></SelectTrigger>
                    <SelectContent><SelectItem value="__none__">No cambia el estado</SelectItem>{states.filter((s) => s.id).map((s) => <SelectItem key={s.id} value={s.id}>→ {s.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <label className="flex items-center gap-1 text-[11px] text-slate-500"><Switch checked={!!r.requires_promise} onCheckedChange={(v) => updResp(i, { requires_promise: v })} />Promesa</label>
                  <label className="flex items-center gap-1 text-[11px] text-slate-500"><Switch checked={!!r.marks_contact_invalid} onCheckedChange={(v) => updResp(i, { marks_contact_invalid: v })} />Invalida contacto</label>
                  <label className="flex items-center gap-1 text-[11px] text-slate-500" title="Cuenta como contacto efectivo en la tasa de contacto"><Switch checked={!!r.is_positive} onCheckedChange={(v) => updResp(i, { is_positive: v })} data-testid={`crm-resp-positive-${r.id || i}`} />Positiva</label>
                </>
              } />
          ))}
        </div>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => setResponses([...responses, { name: '', color: '#64748b', is_active: true }])} data-testid="crm-add-response"><Plus className="w-4 h-4 mr-1" />Agregar respuesta</Button>
      </Card>

      <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="crm-settings-save">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar cambios</Button>
    </div>
  );
};

export default SettingsGestion;
